import type { TFunction } from "i18next";
import type { RouteObject } from "react-router";
import { Navigate } from "react-router";
import { getSampleRoutes } from "src/features/samples/sampleFeature";
import { Error404 } from "src/general/errors/Error404";
import { RootLayout } from "src/general/layout/AppLayout/RootLayout";
import { ACCESS_RULES } from "src/utils/permissionUtils";
import AppLoading from "src/views/login/AppLoading";
import { buildEntitySection, lazyEntityCrumb } from "./entitySectionRoutes";
import { FEAT } from "./features";
import { SECTION_ICONS } from "./icons";
import { lazyRoute } from "./lazyRoutes";
import {
  getAttributeDefinitionRoutes,
  getDataTaskRoutes,
  getLicenceRoutes,
  getLocationRoutes,
  getReaderRoutes,
  getSensorGroupRoutes,
} from "./routes";
import { URLS } from "./urls";

/** The pages a sensor trait has, reached through a sensor or on its own. */
const sensorTraitAddRoute: RouteObject = {
  path: "add",
  lazy: lazyRoute(() => import("src/views/sensorTraits/SensorTraitForm")),
  handle: {
    access: ACCESS_RULES.traitDefinitionsWrite,
    crumb: { label: (t: TFunction) => t("trait::Link trait to sensor") },
  },
};

const sensorTraitPages: RouteObject[] = [
  {
    index: true,
    lazy: lazyRoute(() => import("src/views/sensorTraits/SensorTraitDetails")),
    handle: { access: ACCESS_RULES.traitsRead },
  },
  {
    path: "edit",
    lazy: lazyRoute(() => import("src/views/sensorTraits/SensorTraitForm")),
    handle: {
      access: ACCESS_RULES.traitDefinitionsWrite,
      crumb: { label: (t: TFunction) => t("common::Edit") },
    },
  },
  {
    path: "ingest",
    lazy: lazyRoute(
      () => import("src/views/sensorTraits/SensorTraitIngestForm"),
    ),
    handle: {
      access: ACCESS_RULES.traitValuesWrite,
      crumb: { label: (t: TFunction) => t("trait::Ingest data") },
    },
  },
];

const sensorTraitRoutes: RouteObject[] = [
  sensorTraitAddRoute,
  {
    path: ":traitId",
    lazy: lazyEntityCrumb(() =>
      import("src/views/sensorTraits/sensorTraitHooks").then(
        (module) => module.useSensorTraitCrumbLabel,
      ),
    ),
    children: sensorTraitPages,
  },
];

export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    HydrateFallback: AppLoading,
    children: [
      {
        path: URLS.login,
        lazy: lazyRoute(() => import("src/views/login/Login")),
      },
      {
        lazy: lazyRoute(
          () => import("src/general/layout/AppLayout/AppLayoutBlank"),
        ),
        children: [
          {
            path: URLS.setup,
            lazy: lazyRoute(() => import("src/views/setup/SetupWizard")),
            handle: { access: ACCESS_RULES.admin },
            children: [
              {
                path: URLS.setup,
                element: <Navigate to={URLS.setupGetStarted} />,
              },
              {
                path: URLS.setupGetStarted,
                lazy: lazyRoute(() => import("src/views/setup/GetStarted")),
              },
              ...getLocationRoutes(URLS.setupLocations),
              ...getReaderRoutes(URLS.setupReaders),
              ...getAttributeDefinitionRoutes(URLS.setupAttributeDefinitions),
              ...getDataTaskRoutes(URLS.setupDataTasks),
              ...getSensorGroupRoutes(URLS.setupSensorGroups),
              {
                path: URLS.setupImport,
                lazy: lazyRoute(() => import("src/views/setup/ImportData")),
              },
            ],
          },
        ],
      },
      {
        lazy: lazyRoute(() => import("src/general/layout/AppLayout/AppLayout")),
        children: [
          {
            path: "*",
            element: <Error404 />,
          },
          {
            index: true,
            lazy: lazyRoute(() => import("src/views/overview/Overview")),
          },
          {
            path: URLS.overview,
            lazy: lazyRoute(() => import("src/views/overview/Overview")),
          },
          buildEntitySection({
            path: URLS.alerts,
            icon: SECTION_ICONS.alerts,
            label: (t: TFunction) => t("common::Alerts"),
            list: lazyRoute(() => import("src/views/alerts/AlertList")),
            form: lazyRoute(() => import("src/views/alerts/AlertForm")),
            entityLabel: () =>
              import("src/views/alerts/alertHooks").then(
                (module) => module.useAlertCrumbLabel,
              ),
            access: { write: ACCESS_RULES.alertsWrite },
            // No crumbs on the tab routes: the tabs already name the view, and a
            // trail appearing on two of the three tabs shifts the page below it.
            sectionChildren: [
              {
                path: "acknowledged",
                lazy: lazyRoute(() => import("src/views/alerts/AlertList"), {
                  variant: "acknowledged",
                }),
              },
              {
                path: "historical",
                lazy: lazyRoute(() => import("src/views/alerts/AlertList"), {
                  variant: "historical",
                }),
              },
            ],
          }),
          {
            path: URLS.search,
            lazy: lazyRoute(() => import("src/views/search/SearchResultList")),
          },
          ...getLocationRoutes(),
          ...getSensorGroupRoutes(),
          ...getAttributeDefinitionRoutes(),
          buildEntitySection({
            path: URLS.traitDefinitions,
            icon: SECTION_ICONS.traitDefinitions,
            label: (t: TFunction) => t("common::Trait Definitions"),
            list: lazyRoute(
              () => import("src/views/traitDefinitions/TraitDefinitionList"),
            ),
            details: lazyRoute(
              () => import("src/views/traitDefinitions/TraitDefinitionDetails"),
            ),
            form: lazyRoute(
              () => import("src/views/traitDefinitions/TraitDefinitionForm"),
            ),
            entityLabel: () =>
              import("src/views/traitDefinitions/traitDefinitionHooks").then(
                (module) => module.useTraitDefinitionCrumbLabel,
              ),
            access: {
              read: ACCESS_RULES.traitsRead,
              write: ACCESS_RULES.traitDefinitionsWrite,
            },
          }),
          buildEntitySection({
            path: URLS.resources,
            icon: SECTION_ICONS.resources,
            label: (t: TFunction) => t("common::Resources"),
            list: lazyRoute(() => import("src/views/resources/ResourceList")),
            details: lazyRoute(
              () => import("src/views/resources/ResourceDetails"),
            ),
            form: lazyRoute(() => import("src/views/resources/ResourceForm")),
            entityLabel: () =>
              import("src/views/resources/resourceHooks").then(
                (module) => module.useResourceCrumbLabel,
              ),
            access: {
              read: ACCESS_RULES.resourcesRead,
              write: ACCESS_RULES.resourcesWrite,
            },
            attributeValues: "resource",
            attributeValuesWrite: ACCESS_RULES.resourcesWrite,
          }),
          buildEntitySection({
            path: URLS.locationSequences,
            icon: SECTION_ICONS.locationSequences,
            label: (t: TFunction) => t("common::Location Sequences"),
            list: lazyRoute(
              () => import("src/views/locationSequences/LocationSequenceList"),
            ),
            details: lazyRoute(
              () =>
                import("src/views/locationSequences/LocationSequenceDetails"),
            ),
            form: lazyRoute(
              () => import("src/views/locationSequences/LocationSequenceForm"),
            ),
            entityLabel: () =>
              import("src/views/locationSequences/locationSequenceHooks").then(
                (module) => module.useLocationSequenceCrumbLabel,
              ),
            access: {
              read: ACCESS_RULES.locationSequencesRead,
              write: ACCESS_RULES.locationSequencesWrite,
            },
          }),
          ...getLicenceRoutes(URLS.licences),
          buildEntitySection({
            path: URLS.sensors,
            icon: SECTION_ICONS.sensors,
            label: (t: TFunction) => t("common::Sensors"),
            list: lazyRoute(() => import("src/views/sensors/SensorList")),
            details: lazyRoute(() => import("src/views/sensors/SensorDetails")),
            form: lazyRoute(() => import("src/views/sensors/SensorForm")),
            entityLabel: () =>
              import("src/views/sensors/sensorHooks").then(
                (module) => module.useSensorCrumbLabel,
              ),
            access: {
              read: ACCESS_RULES.sensorsRead,
              write: ACCESS_RULES.sensorsWrite,
            },
            attributeValues: "sensor",
            attributeValuesWrite: ACCESS_RULES.sensorsWrite,
            entityChildren: [
              {
                // No crumb of its own: the sensor above and the trait below are
                // the two names worth showing.
                path: "sensor-traits",
                children: sensorTraitRoutes,
              },
            ],
          }),
          buildEntitySection({
            path: URLS.sensorTraits,
            icon: SECTION_ICONS.sensorTraits,
            label: (t: TFunction) => t("trait::Sensor Traits"),
            list: lazyRoute(
              () => import("src/views/sensorTraits/SensorTraitList"),
            ),
            entityLabel: () =>
              import("src/views/sensorTraits/sensorTraitHooks").then(
                (module) => module.useSensorTraitCrumbLabel,
              ),
            idParam: "traitId",
            access: { read: ACCESS_RULES.traitsRead },
            sectionChildren: [sensorTraitAddRoute],
            entityChildren: sensorTraitPages,
          }),
          ...(FEAT.samples ? getSampleRoutes() : []),
          ...getReaderRoutes(),
          buildEntitySection({
            path: URLS.inventory,
            icon: SECTION_ICONS.inventory,
            label: (t: TFunction) => t("common::Inventory"),
            list: lazyRoute(() => import("src/views/inventory/InventoryList")),
            details: lazyRoute(
              () => import("src/views/inventory/InventoryDetails"),
            ),
            form: lazyRoute(() => import("src/views/inventory/InventoryForm")),
            entityLabel: () =>
              import("src/views/inventory/inventoryHooks").then(
                (module) => module.useInventoryCrumbLabel,
              ),
            // Inventory rows are created by the platform, never by hand.
            noAddPage: true,
            access: {
              read: ACCESS_RULES.inventoryRead,
              write: ACCESS_RULES.inventoryWrite,
            },
          }),
          buildEntitySection({
            path: URLS.attributeValues,
            icon: SECTION_ICONS.attributeValues,
            label: (t: TFunction) => t("common::Attribute Values"),
            list: lazyRoute(
              () => import("src/views/attributeValues/AttributeValueList"),
            ),
            details: lazyRoute(
              () => import("src/subEntities/AttributeValueDetails"),
            ),
            entityLabel: () =>
              import("src/subEntities/attributeValueHooks").then(
                (module) => module.useAttributeValueByIdCrumbLabel,
              ),
            // The form picks the object itself here; every other route reaches
            // it through the object that already names it.
            form: lazyRoute(() => import("src/subEntities/AttributeValueForm")),
            access: {
              read: ACCESS_RULES.dataDefinitionsRead,
              write: ACCESS_RULES.dataDefinitionsWrite,
            },
          }),
          buildEntitySection({
            path: URLS.floorplans,
            icon: SECTION_ICONS.floorplans,
            label: (t: TFunction) => t("common::Floor Plans"),
            list: lazyRoute(() => import("src/views/floorplans/FloorplanList")),
            details: lazyRoute(
              () => import("src/views/floorplans/FloorplanView"),
            ),
            form: lazyRoute(() => import("src/views/floorplans/FloorplanForm")),
            entityLabel: () =>
              import("src/views/floorplans/floorplanHooks").then(
                (module) => module.useFloorplanCrumbLabel,
              ),
            access: {
              read: ACCESS_RULES.floorplansRead,
              write: ACCESS_RULES.floorplansWrite,
            },
          }),
          {
            path: URLS.analytics,
            lazy: lazyRoute(() => import("src/views/analytics/AnalyticsView")),
          },
          buildEntitySection({
            path: URLS.users,
            icon: SECTION_ICONS.users,
            label: (t: TFunction) => t("common::Users"),
            list: lazyRoute(() => import("src/views/users/UserList")),
            form: lazyRoute(() => import("src/views/users/UserForm")),
            entityLabel: () =>
              import("src/views/users/userHooks").then(
                (module) => module.useUserCrumbLabel,
              ),
            access: { read: ACCESS_RULES.admin, write: ACCESS_RULES.admin },
            // A user has no page of their own; the list is where they live.
            entityChildren: [
              { index: true, element: <Navigate to={URLS.users} /> },
            ],
          }),
          buildEntitySection({
            path: URLS.apiKeys,
            icon: SECTION_ICONS.apiKeys,
            label: (t: TFunction) => t("common::API Keys"),
            list: lazyRoute(() => import("src/views/apiKeys/ApiKeyList")),
            details: lazyRoute(() => import("src/views/apiKeys/ApiKeyDetails")),
            form: lazyRoute(() => import("src/views/apiKeys/ApiKeyForm")),
            entityLabel: () =>
              import("src/views/apiKeys/apiKeyHooks").then(
                (module) => module.useApiKeyCrumbLabel,
              ),
            access: { read: ACCESS_RULES.admin, write: ACCESS_RULES.admin },
          }),
          {
            path: URLS.configuration,
            lazy: lazyRoute(
              () => import("src/views/platform/PlatformConfiguration"),
            ),
            handle: { access: ACCESS_RULES.admin },
          },
          ...getDataTaskRoutes(),
          {
            path: URLS.settings,
            lazy: lazyRoute(() => import("src/views/settings/Settings")),
          },
        ],
      },
    ],
  },
];
